import { useState, useRef, useEffect } from "react"; 
import { 
    LOCATION_CONFIG, 
    LOCATION_LIST, 
    TIME_OF_DAY, 
    SEASON, 
    VIEW_MODE, 
    v_Drone,   
    v_Cyclist,
    a_Drone,
    a_Cyclist, 
    DEFAULT_FADE_DURATION_MS, 
    MAP_IMAGE_SRC 
} from "../SceneComponents/VideoConfig.jsx";
import { FullScreenButton } from "./FullScreenButton.jsx";
import { playClickSound } from './SoundEffect';

export function ViewControlsBar({ sceneMedia, handleEnvironmentSwitch, isTransitioning }) {
    
    // Logical state used for routing the correct media
    const actualViewMode = sceneMedia.viewMode || VIEW_MODE.PEDESTRIAN;

    // Visual state frozen during transitions so buttons don't instantly swap on click
    const [displayState, setDisplayState] = useState({
        time: sceneMedia.time,
        season: sceneMedia.season,
        viewMode: actualViewMode
    });

    useEffect(() => {
        if (!isTransitioning) {
            setDisplayState({
                time: sceneMedia.time,
                season: sceneMedia.season,
                viewMode: sceneMedia.viewMode || VIEW_MODE.PEDESTRIAN
            });
        }
    }, [sceneMedia, isTransitioning]);
    
    // Popup open states
    const [isTimePopupOpen, setIsTimePopupOpen] = useState(false);
    const [isSeasonPopupOpen, setIsSeasonPopupOpen] = useState(false);
    const [isViewPopupOpen, setIsViewPopupOpen] = useState(false);

    // Individual hover states for popups and main buttons
    const [isTimeMainHovered, setIsTimeMainHovered] = useState(false);
    const [isSeasonMainHovered, setIsSeasonMainHovered] = useState(false);
    const [isViewMainHovered, setIsViewMainHovered] = useState(false);

    const [isMorningHovered, setIsMorningHovered] = useState(false);
    const [isEveningHovered, setIsEveningHovered] = useState(false);
    const [isSpringHovered, setIsSpringHovered] = useState(false);
    const [isAutumnHovered, setIsAutumnHovered] = useState(false);
    const [isPedestrianHovered, setIsPedestrianHovered] = useState(false);
    const [isBirdHovered, setIsBirdHovered] = useState(false);
    const [isBikeHovered, setIsBikeHovered] = useState(false);

    const timeRef = useRef(null);
    const seasonRef = useRef(null);
    const viewRef = useRef(null);

    // Close popups when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (timeRef.current && !timeRef.current.contains(event.target)) {
                setIsTimePopupOpen(false);
            }
            if (seasonRef.current && !seasonRef.current.contains(event.target)) {
                setIsSeasonPopupOpen(false);
            }
            if (viewRef.current && !viewRef.current.contains(event.target)) {
                setIsViewPopupOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectTimeOfDay = (newTime) => {
        playClickSound();
        setIsTimePopupOpen(false);
        const newMedia = LOCATION_CONFIG[sceneMedia.locId].media[actualViewMode][sceneMedia.season][newTime];
        handleEnvironmentSwitch(newMedia);
    };

   const selectSeason = (newSeason) => {
        playClickSound();
        setIsSeasonPopupOpen(false);
        // Directly swap the season while keeping the current view mode and time
        const newMedia = LOCATION_CONFIG[sceneMedia.locId].media[actualViewMode][newSeason][sceneMedia.time];
        handleEnvironmentSwitch(newMedia);
    };

    const selectViewMode = (mode) => {
        playClickSound();
        setIsViewPopupOpen(false);
        // Directly swap the view mode while keeping the current season and time
        const newMedia = LOCATION_CONFIG[sceneMedia.locId].media[mode][sceneMedia.season][sceneMedia.time];
        handleEnvironmentSwitch(newMedia);
    };
    const getHoverImagePath = (basePath, isHovered) => {
        // Check if the device hardware supports hover interactions
        const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
        
        // Only return the glow path if the button is hovered AND the device supports it
        return (isHovered && canHover) ? basePath.replace('.png', '_Glow.png') : basePath;
    };

    // Icons configuration
    const morningIconPath = "/images/ui/T_Icons/T_Icon_Sunrise.png";
    const eveningIconPath = "/images/ui/T_Icons/T_Icon_Sunset.png";
    const baseTimeIcon = displayState.time === TIME_OF_DAY.EVENING ? eveningIconPath : morningIconPath; 
    const timeIcon = getHoverImagePath(baseTimeIcon, isTimeMainHovered);

    const springIconPath = "/images/ui/T_Icons/T_Icon_Spring.png";
    const autumnIconPath = "/images/ui/T_Icons/T_Icon_Autumn.png";
    const baseSeasonIcon = displayState.season === SEASON.AUTUMN ? autumnIconPath : springIconPath; 
    const seasonIcon = getHoverImagePath(baseSeasonIcon, isSeasonMainHovered);
    
    const pedestrianIconPath = "/images/ui/T_Icons/T_Icon_Pedestrian.png";
    const birdIconPath = "/images/ui/T_Icons/T_Icon_Bird.png";
    const bikeIconPath = "/images/ui/T_Icons/T_Icon_Bike.png";
    
    let baseMainViewIcon = pedestrianIconPath; 
    if (displayState.viewMode === VIEW_MODE.BIRD) baseMainViewIcon = birdIconPath; 
    if (displayState.viewMode === VIEW_MODE.BIKE) baseMainViewIcon = bikeIconPath; 
    const viewModeIcon = getHoverImagePath(baseMainViewIcon, isViewMainHovered);

    return (
        <div className="view-controls-pill">
            {/* Time of Day Popup Container */}
            <div className={`control-dropdown-container ${isTimePopupOpen ? "open" : ""}`} ref={timeRef}>
                <div className="control-dropdown-popup">
                    {displayState.time !== TIME_OF_DAY.MORNING && (
                        <button 
                            type="button" 
                            className="pill-icon-button" 
                            onClick={() => selectTimeOfDay(TIME_OF_DAY.MORNING)}
                            title="Morning"
                            disabled={isTransitioning}
                            onMouseEnter={() => setIsMorningHovered(true)}
                            onMouseLeave={() => setIsMorningHovered(false)}
                        >
                            <img src={getHoverImagePath(morningIconPath, isMorningHovered)} alt="Morning Mode" />
                        </button>
                    )}
                    {displayState.time !== TIME_OF_DAY.EVENING && (
                        <button 
                            type="button" 
                            className="pill-icon-button" 
                            onClick={() => selectTimeOfDay(TIME_OF_DAY.EVENING)}
                            title="Evening"
                            disabled={isTransitioning}
                            onMouseEnter={() => setIsEveningHovered(true)}
                            onMouseLeave={() => setIsEveningHovered(false)}
                        >
                            <img src={getHoverImagePath(eveningIconPath, isEveningHovered)} alt="Evening Mode" />
                        </button>
                    )}
                </div>
                <button 
                    type="button" 
                    className="pill-icon-button" 
                    onClick={() => setIsTimePopupOpen(prev => !prev)} 
                    title="Time of Day" 
                    disabled={isTransitioning}
                    onMouseEnter={() => setIsTimeMainHovered(true)}
                    onMouseLeave={() => setIsTimeMainHovered(false)}
                >                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                             
                    <img src={timeIcon} alt="Toggle Time" />
                </button>
            </div>

            {/* Season Popup Container */}
            <div className={`control-dropdown-container ${isSeasonPopupOpen ? "open" : ""}`} ref={seasonRef}>
                <div className="control-dropdown-popup">
                    {displayState.season !== SEASON.SPRING && (
                        <button 
                            type="button" 
                            className="pill-icon-button" 
                            onClick={() => selectSeason(SEASON.SPRING)}
                            title="Spring"
                            disabled={isTransitioning}
                            onMouseEnter={() => setIsSpringHovered(true)}
                            onMouseLeave={() => setIsSpringHovered(false)}
                        >
                            <img src={getHoverImagePath(springIconPath, isSpringHovered)} alt="Spring Mode" />
                        </button>
                    )}
                    {displayState.season !== SEASON.AUTUMN && (
                        <button 
                            type="button" 
                            className="pill-icon-button" 
                            onClick={() => selectSeason(SEASON.AUTUMN)}
                            title="Autumn"
                            disabled={isTransitioning}
                            onMouseEnter={() => setIsAutumnHovered(true)}
                            onMouseLeave={() => setIsAutumnHovered(false)}
                        >
                            <img src={getHoverImagePath(autumnIconPath, isAutumnHovered)} alt="Autumn Mode" />
                        </button>
                    )}
                </div>
                <button 
                    type="button" 
                    className="pill-icon-button" 
                    onClick={() => setIsSeasonPopupOpen(prev => !prev)} 
                    title="Season" 
                    disabled={isTransitioning}
                    onMouseEnter={() => setIsSeasonMainHovered(true)}
                    onMouseLeave={() => setIsSeasonMainHovered(false)}
                >
                    <img src={seasonIcon} alt="Toggle Season" />
                </button>
            </div>

            {/* View Mode Popup Container */}
            <div className={`control-dropdown-container ${isViewPopupOpen ? "open" : ""}`} ref={viewRef}>
                <div className="control-dropdown-popup">
                    {displayState.viewMode !== VIEW_MODE.PEDESTRIAN && (
                        <button 
                            type="button" 
                            className="pill-icon-button" 
                            onClick={() => selectViewMode(VIEW_MODE.PEDESTRIAN)}
                            title="Pedestrian View"
                            disabled={isTransitioning}
                            onMouseEnter={() => setIsPedestrianHovered(true)}
                            onMouseLeave={() => setIsPedestrianHovered(false)}
                        >
                            <img src={getHoverImagePath(pedestrianIconPath, isPedestrianHovered)} alt="Pedestrian Mode" />
                        </button>
                    )}

                    {displayState.viewMode !== VIEW_MODE.BIRD && (
                        <button 
                            type="button" 
                            className="pill-icon-button" 
                            onClick={() => selectViewMode(VIEW_MODE.BIRD)}
                            title="Bird View"
                            disabled={isTransitioning}
                            onMouseEnter={() => setIsBirdHovered(true)}
                            onMouseLeave={() => setIsBirdHovered(false)}
                        >
                            <img src={getHoverImagePath(birdIconPath, isBirdHovered)} alt="Bird Mode" />
                        </button>
                    )}

                    {displayState.viewMode !== VIEW_MODE.BIKE && (
                        <button 
                            type="button" 
                            className="pill-icon-button" 
                            onClick={() => selectViewMode(VIEW_MODE.BIKE)}
                            title="Bike View"
                            disabled={isTransitioning}
                            onMouseEnter={() => setIsBikeHovered(true)}
                            onMouseLeave={() => setIsBikeHovered(false)}
                        >
                            <img src={getHoverImagePath(bikeIconPath, isBikeHovered)} alt="Bike Mode" />
                        </button>
                    )}
                </div>
                <button 
                    type="button" 
                    className="pill-icon-button" 
                    onClick={() => setIsViewPopupOpen(prev => !prev)} 
                    title="View Mode" 
                    disabled={isTransitioning}
                    onMouseEnter={() => setIsViewMainHovered(true)}
                    onMouseLeave={() => setIsViewMainHovered(false)}
                >
                    <img src={viewModeIcon} alt="Select View Mode" />
                </button>
            </div>
        </div>
    );
}

export function UI({ sceneMedia, setSceneMedia }) {
    const [isReady, setIsReady] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);    

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsReady(true);
            console.debug("UI is now ready and visible.");
        }, DEFAULT_FADE_DURATION_MS);
        return () => clearTimeout(timer);
    }, []);
    
    const handleEnvironmentSwitch = (newSceneMedia) => {
        console.debug(`Switching to: Location ${newSceneMedia.locId} | ${newSceneMedia.season} | ${newSceneMedia.time}`);

        setIsTransitioning(true);
        playClickSound(); 
        
        if (isTransitioning) {
            console.debug("Double click ignored: Transition already in progress.");
            return; 
        }

        console.debug(`Switching to: Location ${newSceneMedia.locId} | ${newSceneMedia.season} | ${newSceneMedia.time}`);
        
        setSceneMedia(prev => ({
            ...newSceneMedia,        
            audioSrc: prev.audioSrc  
        }));

        setTimeout(() => {
            setSceneMedia(newSceneMedia);
            setTimeout(() => {
                setIsTransitioning(false);
            }, DEFAULT_FADE_DURATION_MS);
        }, DEFAULT_FADE_DURATION_MS);
    };

    return (
        <div 
            className="ui-container"
            style={{
                opacity: (isReady && !isTransitioning) ? 1 : 0,        
                transition: 'opacity 0.4s ease-in-out' 
            }}
        >
            <div className="util-buttons">
                <FullScreenButton />
            </div>
            
            <div className="top-center-map-overlay">
                <img src={MAP_IMAGE_SRC} alt="Tribschenstrasse Map" className="map-overlay-graphic" />
                
                {LOCATION_LIST.map((loc) => (
                    <button
                        key={loc.id}
                        className={`ui-map-hotspot ${sceneMedia.locId === loc.id ? 'active' : ''}`}
                        style={{ top: loc.top, left: loc.left }}
                        onClick={() => {
                            const defaultMedia = LOCATION_CONFIG[loc.id].media[VIEW_MODE.PEDESTRIAN][loc.defaultSeason][loc.defaultTime];     
                            handleEnvironmentSwitch(defaultMedia);
                        }}
                    >
                        {sceneMedia.locId === loc.id && <span className="active-point-ring" />}
                    </button>
                ))}
            </div>
            
            <div className="view-controls-wrapper">
                 <ViewControlsBar 
                     sceneMedia={sceneMedia} 
                     isTransitioning={isTransitioning}
                     handleEnvironmentSwitch={handleEnvironmentSwitch} 
                 />
            </div>
        </div>
    );
}